import { useEffect, useState, useContext, useRef } from "react";
import EmpIdCard from "../../components/EmpIdCard";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { Pagination, Select, Row, Col, Button } from "antd";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";


export default function EmployeeIdCards() {
    const { authData, backUrl } = useContext(AuthContext);

    const [employees, setEmployees] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [total, setTotal] = useState(0);

    const pdfRef = useRef();

    // Filters
    const [organizations, setOrganizations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);

    // -------------------------------
    // Fetch organisation list
    // -------------------------------
    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setOrganizations(res.data.results || []);
        } catch (err) {
            console.log("Failed to load organisations", err);
        }
    };

    // -------------------------------
    // Fetch departments by organisation
    // -------------------------------
    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation_id=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartments(res.data.results || []);
        } catch (err) {
            console.log("Dept fetch failed", err);
        }
    };

    // -------------------------------
    // Fetch designations by department
    // -------------------------------
    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignations(res.data.results || []);
        } catch (err) {
            console.log("Designation fetch failed", err);
        }
    };

    useEffect(() => {
        fetchOrganisations();
    }, []);

    useEffect(() => {
        loadEmployees(page);
    }, [page, selectedOrg, selectedDept, selectedDesig]);

    const downloadPDF = async () => {
        const element = pdfRef.current;

        // Wait for all images
        const images = element.getElementsByTagName("img");
        await Promise.all(
            Array.from(images).map(img => {
                if (!img.complete) {
                    return new Promise(resolve => {
                        img.onload = resolve;
                        img.onerror = resolve;
                    });
                }
            })
        );

        const canvas = await html2canvas(element, {
            scale: 2,
            useCORS: true,
            allowTaint: false,
        });

        const imgData = canvas.toDataURL("image/png");

        const pdf = new jsPDF("p", "mm", "a4");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save("employee_id_cards.pdf");
    };


    // ------------------------------------------------------
    // Load paginated employee list with filters applied
    // ------------------------------------------------------
    const loadEmployees = async (pageNumber) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/employees/`, {
                params: {
                    page: pageNumber,
                    page_size: pageSize,
                    organisation: selectedOrg || "",
                    department: selectedDept || "",
                    designation: selectedDesig || "",
                },
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            setEmployees(res.data?.results || []);
            setTotal(res.data?.count || 0);
        } catch (err) {
            console.error("Failed to load employees", err);
        }
    };

    return (
        <>
            <center><h5>Employee ID Cards</h5>
                <Button
                    type="primary"
                    onClick={downloadPDF}
                    style={{ marginBottom: 20 }}
                >
                    Download PDF
                </Button>
            </center>

            {/* FILTERS */}
            <Row
                gutter={20}
                style={{
                    marginTop: 20,
                    marginBottom: 20,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                {/* Organisation Filter */}
                <Col xs={24} md={6}>
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Select Organisation"
                        value={selectedOrg}
                        onChange={(v) => {
                            setSelectedOrg(v);
                            setSelectedDept(null);
                            setSelectedDesig(null);
                            setDepartments([]);
                            setDesignations([]);
                            fetchDepartments(v);
                            setPage(1);
                        }}
                        allowClear
                    >
                        {organizations.map((o) => (
                            <Select.Option key={o.id} value={o.id}>
                                {o.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                {/* Department Filter */}
                <Col xs={24} md={6}>
                    <Select
                        showSearch
                        style={{ width: "100%" }}
                        placeholder="Select Department"
                        value={selectedDept}
                        onChange={(v) => {
                            setSelectedDept(v);
                            setSelectedDesig(null);
                            setDesignations([]);
                            fetchDesignations(v);
                            setPage(1);
                        }}
                        disabled={!selectedOrg}
                        allowClear
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {departments.map((d) => (
                            <Select.Option key={d.id} value={d.id}>
                                {d.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                {/* Designation Filter */}
                <Col xs={24} md={6}>
                    <Select
                        showSearch
                        style={{ width: "100%" }}
                        placeholder="Select Designation"
                        value={selectedDesig}
                        onChange={(v) => {
                            setSelectedDesig(v);
                            setPage(1);
                        }}
                        disabled={!selectedDept}
                        allowClear
                        filterOption={(input, option) =>
                            option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {designations.map((ds) => (
                            <Select.Option key={ds.id} value={ds.id}>
                                {ds.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>
            </Row>

            {/* Cards */}
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "40px",
                }}
                id="pdf-area"
                ref={pdfRef}
            >
                {employees.map((emp) => (
                    <EmpIdCard key={emp.id} employee={emp} />
                ))}
            </div>

            {/* Pagination */}
            <div style={{ textAlign: "center", marginTop: 30 }}>
                <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={total}
                    onChange={(p) => setPage(p)}
                    showSizeChanger={false}
                />
            </div>
        </>
    );
}
